// frontend/src/hooks/useLicence.js
// ============================================================================
// HOOK PERSONNALISÉ - GESTION DE LA LICENCE
// ============================================================================

import { useState, useEffect } from 'react';
import licenceApi from '../services/licenceApi';

/**
 * Hook pour récupérer et gérer la licence de l'entreprise
 * @returns {Object} { licence, loading, error, refetch, getStatutLicence }
 */
export const useLicence = () => {
  const [licence, setLicence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ========================================================================
  // CHARGER LA LICENCE
  // ========================================================================
  
  const loadLicence = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📥 [Hook Licence] Chargement de la licence...');

      const response = await licenceApi.getMaLicence();
      
      if (response.success) {
        console.log('✅ [Hook Licence] Licence chargée:', response.data.numero_licence);
        setLicence(response.data);
      } else {
        throw new Error(response.message || 'Erreur lors du chargement de la licence');
      }

    } catch (err) {
      console.error('❌ [Hook Licence] Erreur:', err);
      setError(err.message || 'Erreur lors du chargement de la licence');
      setLicence(null);
    } finally {
      setLoading(false);
    }
  };

  // ========================================================================
  // CHARGER AU MONTAGE DU COMPOSANT
  // ========================================================================
  
  useEffect(() => {
    loadLicence();
  }, []);

  // ========================================================================
  // FONCTION POUR DÉTERMINER LE STATUT VISUEL
  // ========================================================================
  
  const getStatutLicence = () => {
    if (!licence) {
      return {
        label: 'Aucune licence',
        color: '#ef4444',
        bgColor: '#fee2e2'
      };
    }

    switch (licence.statut) {
      case 'active':
        return {
          label: 'Active',
          color: '#22c55e',
          bgColor: '#dcfce7'
        };
      case 'expire_bientot':
        return {
          label: 'Expire bientôt',
          color: '#f59e0b',
          bgColor: '#fef3c7'
        };
      case 'expiree':
        return {
          label: 'Expirée',
          color: '#ef4444',
          bgColor: '#fee2e2'
        };
      case 'suspendue':
        return {
          label: 'Suspendue',
          color: '#6b7280',
          bgColor: '#f3f4f6'
        };
      default:
        return {
          label: 'Statut inconnu',
          color: '#6b7280',
          bgColor: '#f3f4f6'
        };
    }
  };

  // ========================================================================
  // FONCTION POUR VÉRIFIER UNE LIMITE
  // ========================================================================
  
  const verifierLimite = async (type = 'employe') => {
    try {
      const response = await licenceApi.verifierLimite(type);
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Erreur lors de la vérification');
      }
    } catch (err) {
      console.error(`❌ [Hook Licence] Erreur vérification ${type}:`, err);
      return {
        autorise: false,
        limite_atteinte: true,
        message: 'Erreur de vérification'
      };
    }
  };

  // ========================================================================
  // RETOUR DU HOOK
  // ========================================================================
  
  return {
    licence,
    loading,
    error,
    refetch: loadLicence,
    getStatutLicence,
    verifierLimite
  };
};

export default useLicence;